import {
  ALL_SDK_NETWORKS,
  MAX_VAULTS_PER_ASSET,
  MINIMUM_BALANCE_THRESHOLD,
  MINIMUM_VAULT_TVL,
  MIN_VAULT_SCORE,
} from '@/config/query/queryParams'
import { DEFAULT_SETTINGS } from '@/context/settings'
import type { VaultApyRange } from '@/types/apy'
import type { Vault } from '@/types/vault'
import { getDefiSdk } from '@/utils/defiSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { Address } from 'viem'

const bestDepositOptionsQueryKey = ['bestDepositOptions'] as const

interface StaticDepositOptionsQueryParams {
  address: Address | undefined
  allowedNetworks?: Vault['network']['name'][]
  apyInterval?: VaultApyRange
}

interface DynamicDepositOptionsQueryParams {
  allowedAssets?: string[]
  maxVaultsPerAsset?: number
  minTvl?: number
  minUsdAssetValueThreshold?: number
  enabled?: boolean
}

type DepositOptionsQueryProps = StaticDepositOptionsQueryParams & DynamicDepositOptionsQueryParams

function bestDepositOptionsQueryOptions({
  address,
  allowedAssets,
  allowedNetworks = ALL_SDK_NETWORKS,
  maxVaultsPerAsset = MAX_VAULTS_PER_ASSET,
  apyInterval = DEFAULT_SETTINGS.apyRange,
  minUsdAssetValueThreshold = MINIMUM_BALANCE_THRESHOLD,
  enabled: externalEnabled,
}: DepositOptionsQueryProps) {
  return queryOptions({
    queryKey: [
      ...bestDepositOptionsQueryKey,
      {
        userAddress: address,
        allowedNetworks,
        allowedAssets,
        maxVaultsPerAsset,
        apyInterval,
        minUsdAssetValueThreshold,
      },
    ],
    async queryFn() {
      if (!address) return null
      const defiSdk = getDefiSdk()
      return await defiSdk.getDepositOptions({
        path: { userAddress: address },
        query: {
          allowedNetworks,
          allowedAssets,
          minTvl: MINIMUM_VAULT_TVL,
          onlyAppFeatured: true,
          apyInterval,
          minUsdAssetValueThreshold,
          maxVaultsPerAsset,
          minVaultScore: MIN_VAULT_SCORE,
        },
      })
    },
    select(data) {
      if (data == null) {
        return null
      }
      const depositOptions = data.userBalances
        .flatMap((userBalance) => userBalance.depositOptions)
        .sort((a, b) => {
          if (Number(b.projectedUsdAnnualEarnings) !== Number(a.projectedUsdAnnualEarnings)) {
            return Number(b.projectedUsdAnnualEarnings) - Number(a.projectedUsdAnnualEarnings)
          }

          return b.apy.total - a.apy.total
        })

      return depositOptions
    },
    enabled: !!address && (externalEnabled ?? true),
    retry: 2,
    staleTime: 2 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: true,
    placeholderData: undefined,
  })
}

export const useBestDepositOptionsQuery = ({
  address,
  allowedNetworks,
  allowedAssets,
  maxVaultsPerAsset,
  minTvl,
  minUsdAssetValueThreshold,
  enabled,
  apyInterval,
  initialData,
}: DepositOptionsQueryProps & { initialData: ReturnType<typeof bestDepositOptionsQueryOptions>['initialData'] }) => {
  const { data, isFetching } = useQuery({
    ...bestDepositOptionsQueryOptions({
      allowedNetworks,
      allowedAssets,
      minTvl,
      minUsdAssetValueThreshold,
      enabled,
      apyInterval,
      maxVaultsPerAsset,
      address,
    }),
    initialData,
  })
  return { data, isFetching }
}

export type BestDepositOptions = ReturnType<typeof useBestDepositOptionsQuery>['data']
